import { useEffect, useMemo, useState } from 'react';
import { Box } from '@mui/material';
import { FilterAlt, FilterAltOff } from '@mui/icons-material';

import {
  CustomAutoComplete,
  CustomButton,
  CustomTextField,
  HorizontalForm,
  StatusChip,
} from '../../../common/components';
import { COLUMN_LABELS } from '../../../common/components/materials/materialPresentationConstants';
import { COLUMN_FILTER_KIND, MATERIAL_TEXTS } from '../constants/materialDashboardConstants';
import { getColumnFilterKind } from '../utils/getColumnFilterKind';
import { getColumnFilterLabel } from '../utils/getColumnFilterLabel';
import { getColumnFilterOperators } from '../utils/getColumnFilterOperators';
import { getFixedFilterFieldSx } from '../../utils/getFixedFilterFieldSx';
import { materialCell } from '../../../utils/materialDisplay';

const EXACT_OPERATOR = 'eq';

const toolbarSx = {
  px: 2.5,
  py: 1.5,
  backgroundColor: 'background.paper',
  gap: 1.5,
  alignItems: 'center',
  flexWrap: 'wrap',
  overflowX: 'visible',
  borderBottom: '1px solid',
  borderColor: 'divider',
};

/**
 * Filtros por columna de una tabla del tablero: texto (igual o contiene), número o fecha (igual o
 * comparador). Cada columna ofrece solo los operadores que la API acepta para su tipo.
 * @param {Object} props
 * @param {Array<Object>} props.value Filtros aplicados `{ column, operator, value }`.
 * @param {Function} props.onChange Recibe la lista nueva de filtros.
 * @param {Array<Object>} [props.rows] Renglones visibles, para sugerir valores exactos.
 * @param {Object} [props.options] Valores posibles por columna informados por el backend.
 * @param {Array<string>} props.allowedColumns Columnas filtrables (las de la tabla).
 * @returns {JSX.Element}
 */
const MaterialColumnFilters = ({ value, onChange, rows = [], options = {}, allowedColumns }) => {
  const columns = useMemo(() => allowedColumns || [], [allowedColumns]);
  const [column, setColumn] = useState(columns[0]);
  const [operator, setOperator] = useState(EXACT_OPERATOR);
  const [term, setTerm] = useState('');
  const kind = getColumnFilterKind(column);
  const operatorOptions = getColumnFilterOperators(column);

  useEffect(() => {
    if (!columns.includes(column)) {
      setColumn(columns[0]);
      setOperator(EXACT_OPERATOR);
      setTerm('');
    }
  }, [column, columns]);

  const exactOptions = (
    options?.[column] ||
    [...new Set((rows || []).map((row) => row[column]).filter((item) => item != null))]
  ).map((item) => ({ value: String(item), label: String(materialCell(column, item)) }));

  const changeColumn = (event) => {
    setColumn(event.target.value || columns[0]);
    setOperator(EXACT_OPERATOR);
    setTerm('');
  };

  const applyFilter = () => {
    onChange([
      ...value.filter((filter) => filter.column !== column),
      { column, operator, value: term },
    ]);
    setTerm('');
  };

  const removeFilter = (filterColumn) => {
    onChange(value.filter((filter) => filter.column !== filterColumn));
  };

  return (
    <Box>
      <HorizontalForm withBorder={false} sx={toolbarSx}>
        <CustomAutoComplete
          label={MATERIAL_TEXTS.tableColumn}
          value={column}
          options={columns.map((key) => ({ value: key, label: COLUMN_LABELS[key] || key }))}
          onChange={changeColumn}
          sx={getFixedFilterFieldSx(220)}
        />
        <CustomAutoComplete
          label={MATERIAL_TEXTS.comparison}
          value={operator}
          options={operatorOptions}
          onChange={(event) => setOperator(event.target.value || EXACT_OPERATOR)}
          sx={getFixedFilterFieldSx(198)}
        />
        {kind === COLUMN_FILTER_KIND.date ? (
          <CustomTextField
            label={MATERIAL_TEXTS.dateValue}
            type="date"
            value={term}
            onChange={(event) => setTerm(event.target.value)}
            shrinkLabel
            sx={getFixedFilterFieldSx(240)}
          />
        ) : operator === EXACT_OPERATOR ? (
          <CustomAutoComplete
            label={MATERIAL_TEXTS.exactValue}
            value={term}
            options={exactOptions}
            onChange={(event) => setTerm(event.target.value)}
            sx={getFixedFilterFieldSx(240)}
          />
        ) : (
          <CustomTextField
            label={MATERIAL_TEXTS.rawValue}
            value={term}
            onChange={(event) => setTerm(event.target.value)}
            sx={getFixedFilterFieldSx(240)}
          />
        )}
        <CustomButton
          variant="base"
          startIcon={<FilterAlt />}
          disabled={!term}
          onClick={applyFilter}
        >
          {MATERIAL_TEXTS.apply}
        </CustomButton>
        <CustomButton
          variant="outlined"
          startIcon={<FilterAltOff />}
          disabled={!value.length}
          onClick={() => onChange([])}
        >
          {MATERIAL_TEXTS.clearColumnFilters(value.length)}
        </CustomButton>
        {value.map((filter) => (
          <StatusChip
            key={filter.column}
            color="info"
            label={getColumnFilterLabel(filter)}
            onDelete={() => removeFilter(filter.column)}
          />
        ))}
      </HorizontalForm>
    </Box>
  );
};

export default MaterialColumnFilters;
